import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Image from '@tiptap/extension-image'
import Link from '@tiptap/extension-link'
import Underline from '@tiptap/extension-underline'
import TextAlign from '@tiptap/extension-text-align'
import Placeholder from '@tiptap/extension-placeholder'
import { Callout } from './extensions/CalloutExtension'
import { YouTube } from './extensions/YouTubeExtension'
import { HorizontalRuleEmail } from './extensions/HorizontalRuleExtension'
import { useRef, useState, useCallback, useEffect } from 'react'
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Image as ImageIcon,
  Link as LinkIcon,
  Unlink,
  Undo,
  Redo,
  Loader2,
  Upload,
  X,
  Youtube,
  Play,
  Minus,
  Braces
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { useToast } from '@/components/ui/use-toast'
import { emailService } from '@/services/emailService'
import { cn } from '@/lib/utils'

const FLOATING_MENU_OFFSET = 45

// Toolbar button - component dışında tanımlandı (memoization için)
const ToolbarButton = ({
  onClick,
  onMouseDown,
  isActive = false,
  disabled = false,
  children,
  title,
  size = 'normal'
}: {
  onClick?: () => void
  onMouseDown?: (e: React.MouseEvent) => void
  isActive?: boolean
  disabled?: boolean
  children: React.ReactNode
  title: string
  size?: 'normal' | 'small'
}) => (
  <Button
    type="button"
    variant="ghost"
    size="sm"
    onClick={onClick}
    onMouseDown={onMouseDown}
    disabled={disabled}
    title={title}
    className={cn(
      size === 'small' ? 'h-7 w-7 p-0' : 'h-8 w-8 p-0',
      isActive && (size === 'small' ? 'bg-slate-200 dark:bg-slate-600' : 'bg-slate-200 dark:bg-slate-700')
    )}
  >
    {children}
  </Button>
)

interface RichTextEditorProps {
  content: string
  onChange: (html: string) => void
  placeholder?: string
}

export function RichTextEditor({ content, onChange, placeholder = 'İçeriğinizi buraya yazın...' }: RichTextEditorProps) {
  const { toast } = useToast()
  const [isUploading, setIsUploading] = useState(false)
  const [linkUrl, setLinkUrl] = useState('')
  const [imageUrl, setImageUrl] = useState('')
  const [isLinkPopoverOpen, setIsLinkPopoverOpen] = useState(false)
  const [isImagePopoverOpen, setIsImagePopoverOpen] = useState(false)
  const [isToolbarYoutubePopoverOpen, setIsToolbarYoutubePopoverOpen] = useState(false)
  const [youtubeUrl, setYoutubeUrl] = useState('')
  const [isVariablePopoverOpen, setIsVariablePopoverOpen] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const editorContainerRef = useRef<HTMLDivElement>(null)
  const [floatingMenuTop, setFloatingMenuTop] = useState<number | null>(null)
  const [showFloatingMenu, setShowFloatingMenu] = useState(false)
  const isMouseDownRef = useRef(false)

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
        paragraph: {
          HTMLAttributes: {
            class: 'paragraph-spacing',
          },
        },
        bulletList: {
          HTMLAttributes: {
            class: 'bullet-list-spacing',
          },
        },
        orderedList: {
          HTMLAttributes: {
            class: 'ordered-list-spacing',
          },
        },
        horizontalRule: false, // Custom extension kullanıyoruz
      }),
      Underline,
      Callout,
      YouTube,
      HorizontalRuleEmail,
      Image.configure({
        HTMLAttributes: {
          class: 'email-image',
          style: 'max-width: 600px; width: 100%; height: auto; border-radius: 8px; display: block; margin: 16px auto;',
        },
        allowBase64: true,
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-blue-600 underline hover:text-blue-800',
        },
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Placeholder.configure({
        placeholder,
      }),
    ],
    content,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML())
    },
    onSelectionUpdate: ({ editor }) => {
      const { from, to } = editor.state.selection
      const hasTextSelection = from !== to && !editor.state.selection.empty

      if (hasTextSelection && editorContainerRef.current) {
        const selection = window.getSelection()
        if (selection && selection.rangeCount > 0) {
          const range = selection.getRangeAt(0)
          const rect = range.getBoundingClientRect()
          const containerRect = editorContainerRef.current.getBoundingClientRect()
          setFloatingMenuTop(Math.max(0, rect.top - containerRect.top - FLOATING_MENU_OFFSET))
        }
        // Mouse basılı değilse göster (keyboard selection için)
        if (!isMouseDownRef.current) {
          setShowFloatingMenu(true)
        }
      } else {
        setShowFloatingMenu(false)
        setFloatingMenuTop(null)
      }
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none min-h-[200px] p-3',
      },
    },
  })

  // Content prop değiştiğinde editor'ü güncelle
  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content)
    }
  }, [content, editor])

  // Mouse event listener - menüyü seçim bittiğinde göster
  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      if (editorContainerRef.current?.contains(e.target as Node)) {
        isMouseDownRef.current = true
        setShowFloatingMenu(false)
      }
    }

    const handleMouseUp = () => {
      if (!isMouseDownRef.current) return
      isMouseDownRef.current = false

      // Kısa gecikme ile seçim kontrolü yap
      setTimeout(() => {
        const selection = window.getSelection()
        if (selection && selection.rangeCount > 0 && !selection.isCollapsed && editorContainerRef.current) {
          const range = selection.getRangeAt(0)
          const rect = range.getBoundingClientRect()
          const containerRect = editorContainerRef.current.getBoundingClientRect()
          setFloatingMenuTop(Math.max(0, rect.top - containerRect.top - FLOATING_MENU_OFFSET))
          setShowFloatingMenu(true)
        }
      }, 10)
    }

    document.addEventListener('mousedown', handleMouseDown)
    document.addEventListener('mouseup', handleMouseUp)
    return () => {
      document.removeEventListener('mousedown', handleMouseDown)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [])

  const handleImageUpload = useCallback(async (file: File) => {
    if (file.size > 5 * 1024 * 1024) {
      toast({
        variant: 'destructive',
        title: 'Hata',
        description: 'Resim boyutu 5MB\'dan küçük olmalıdır.'
      })
      return
    }

    try {
      setIsUploading(true)
      const response = await emailService.uploadImage(file)

      if (response.data.status === 'success') {
        const url = response.data.data.url
        editor?.chain().focus().setImage({ src: url }).run()
        setShowFloatingMenu(false)
        toast({
          title: 'Başarılı',
          description: 'Resim eklendi.'
        })
      }
    } catch (error) {
      console.error('Resim yükleme hatası:', error)
      toast({
        variant: 'destructive',
        title: 'Hata',
        description: 'Resim yüklenirken bir hata oluştu.'
      })
    } finally {
      setIsUploading(false)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }, [editor, toast])

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      handleImageUpload(file)
    }
  }, [handleImageUpload])

  const addImageFromUrl = useCallback(() => {
    if (imageUrl) {
      editor?.chain().focus().setImage({ src: imageUrl }).run()
      setShowFloatingMenu(false)
      setImageUrl('')
      setIsImagePopoverOpen(false)
    }
  }, [editor, imageUrl])

  const setLink = useCallback(() => {
    if (linkUrl) {
      editor?.chain().focus().extendMarkRange('link').setLink({ href: linkUrl }).run()
      setLinkUrl('')
      setIsLinkPopoverOpen(false)
    }
  }, [editor, linkUrl])

  const removeLink = useCallback(() => {
    editor?.chain().focus().unsetLink().run()
  }, [editor])

  // YouTube URL'sinden video ID çıkarma
  const extractYoutubeId = (url: string): string | null => {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s?]+)/,
      /youtube\.com\/shorts\/([^&\s?]+)/,
      /youtube\.com\/live\/([^&\s?]+)/
    ]
    for (const pattern of patterns) {
      const match = url.match(pattern)
      if (match) return match[1]
    }
    return null
  }

  // YouTube video ekleme (custom TipTap node olarak)
  const addYoutubeVideo = useCallback(() => {
    if (!youtubeUrl) return

    const videoId = extractYoutubeId(youtubeUrl)
    if (!videoId) {
      toast({
        variant: 'destructive',
        title: 'Hata',
        description: 'Geçerli bir YouTube URL\'si girin.'
      })
      return
    }

    // Custom YouTube node olarak ekle - TipTap yapıyı koruyacak
    editor?.chain().focus().setYouTubeVideo({ videoId }).run()
    setYoutubeUrl('')
    setIsToolbarYoutubePopoverOpen(false)
    setShowFloatingMenu(false)

    toast({
      title: 'Başarılı',
      description: 'YouTube videosu eklendi.'
    })
  }, [editor, youtubeUrl, toast])

  // Değişken ekleme fonksiyonu
  const insertVariable = useCallback((variable: string) => {
    editor?.chain().focus().insertContent(variable).run()
    setIsVariablePopoverOpen(false)
  }, [editor])

  if (!editor) {
    return null
  }

  return (
    <div className="border rounded-lg overflow-hidden">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-0.5 p-2 border-b bg-slate-50 dark:bg-slate-900">
        {/* Undo/Redo */}
        <ToolbarButton
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          title="Geri Al"
        >
          <Undo className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          title="İleri Al"
        >
          <Redo className="h-4 w-4" />
        </ToolbarButton>

        <div className="w-px h-6 bg-slate-300 mx-1" />

        {/* Text Formatting */}
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          isActive={editor.isActive('bold')}
          title="Kalın"
        >
          <Bold className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          isActive={editor.isActive('italic')}
          title="İtalik"
        >
          <Italic className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          isActive={editor.isActive('underline')}
          title="Altı Çizili"
        >
          <UnderlineIcon className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleStrike().run()}
          isActive={editor.isActive('strike')}
          title="Üstü Çizili"
        >
          <Strikethrough className="h-4 w-4" />
        </ToolbarButton>

        <div className="w-px h-6 bg-slate-300 mx-1" />

        {/* Headings */}
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          isActive={editor.isActive('heading', { level: 1 })}
          title="Başlık 1"
        >
          <Heading1 className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          isActive={editor.isActive('heading', { level: 2 })}
          title="Başlık 2"
        >
          <Heading2 className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          isActive={editor.isActive('heading', { level: 3 })}
          title="Başlık 3"
        >
          <Heading3 className="h-4 w-4" />
        </ToolbarButton>

        <div className="w-px h-6 bg-slate-300 mx-1" />

        {/* Lists */}
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          isActive={editor.isActive('bulletList')}
          title="Madde İşaretli Liste"
        >
          <List className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          isActive={editor.isActive('orderedList')}
          title="Numaralı Liste"
        >
          <ListOrdered className="h-4 w-4" />
        </ToolbarButton>

        <div className="w-px h-6 bg-slate-300 mx-1" />

        {/* Alignment */}
        <ToolbarButton
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          isActive={editor.isActive({ textAlign: 'left' })}
          title="Sola Hizala"
        >
          <AlignLeft className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          isActive={editor.isActive({ textAlign: 'center' })}
          title="Ortala"
        >
          <AlignCenter className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          isActive={editor.isActive({ textAlign: 'right' })}
          title="Sağa Hizala"
        >
          <AlignRight className="h-4 w-4" />
        </ToolbarButton>

        <div className="w-px h-6 bg-slate-300 mx-1" />

        {/* Link */}
        <Popover open={isLinkPopoverOpen} onOpenChange={setIsLinkPopoverOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              title="Link Ekle"
              className={cn(
                'h-8 w-8 p-0',
                editor.isActive('link') && 'bg-slate-200 dark:bg-slate-700'
              )}
            >
              <LinkIcon className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80">
            <div className="space-y-2">
              <p className="text-sm font-medium">Link Ekle</p>
              <Input
                placeholder="https://example.com"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && setLink()}
              />
              <div className="flex gap-2">
                <Button size="sm" onClick={setLink} className="flex-1">
                  Ekle
                </Button>
                {editor.isActive('link') && (
                  <Button size="sm" variant="destructive" onClick={removeLink}>
                    <Unlink className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          </PopoverContent>
        </Popover>

        <div className="w-px h-6 bg-slate-300 mx-1" />

        {/* Image */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
          onChange={handleFileSelect}
          className="hidden"
        />

        <Popover open={isImagePopoverOpen} onOpenChange={setIsImagePopoverOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              title="Resim Ekle"
              disabled={isUploading}
              className="h-8 w-8 p-0"
            >
              {isUploading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ImageIcon className="h-4 w-4" />
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80">
            <div className="space-y-3">
              <p className="text-sm font-medium">Resim Ekle</p>

              {/* Upload Button */}
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => {
                  fileInputRef.current?.click()
                  setIsImagePopoverOpen(false)
                }}
                disabled={isUploading}
              >
                <Upload className="h-4 w-4 mr-2" />
                Bilgisayardan Yükle
              </Button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-muted-foreground">veya</span>
                </div>
              </div>

              {/* URL Input */}
              <Input
                placeholder="Resim URL'si girin"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addImageFromUrl()}
              />
              <Button size="sm" onClick={addImageFromUrl} className="w-full">
                URL'den Ekle
              </Button>
            </div>
          </PopoverContent>
        </Popover>

        {/* YouTube */}
        <Popover open={isToolbarYoutubePopoverOpen} onOpenChange={setIsToolbarYoutubePopoverOpen} modal={false}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              title="YouTube Video Ekle"
              className="h-8 w-8 p-0"
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
                setIsToolbarYoutubePopoverOpen(true)
              }}
            >
              <Youtube className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80">
            <div className="space-y-3">
              <p className="text-sm font-medium">YouTube Video Ekle</p>
              <p className="text-xs text-muted-foreground">
                Email'lerde video oynatılamaz. Video thumbnail ve play butonu olarak eklenir, tıklandığında YouTube'a yönlendirir.
              </p>
              <Input
                placeholder="https://www.youtube.com/watch?v=..."
                value={youtubeUrl}
                onChange={(e) => setYoutubeUrl(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    addYoutubeVideo()
                    setIsToolbarYoutubePopoverOpen(false)
                  }
                }}
                autoFocus
              />
              <Button size="sm" onClick={() => {
                addYoutubeVideo()
                setIsToolbarYoutubePopoverOpen(false)
              }} className="w-full">
                <Play className="h-4 w-4 mr-2" />
                Video Ekle
              </Button>
            </div>
          </PopoverContent>
        </Popover>

        <div className="w-px h-6 bg-slate-300 mx-1" />

        {/* Horizontal Rule */}
        <ToolbarButton
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          title="Yatay Çizgi"
        >
          <Minus className="h-4 w-4" />
        </ToolbarButton>

        <div className="w-px h-6 bg-slate-300 mx-1" />

        {/* Variables */}
        <Popover open={isVariablePopoverOpen} onOpenChange={setIsVariablePopoverOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              title="Değişken Ekle"
              className="h-8 w-8 p-0"
            >
              <Braces className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64">
            <div className="space-y-2">
              <p className="text-sm font-medium">Değişken Ekle</p>
              <p className="text-xs text-muted-foreground mb-3">
                Tıklayarak içeriğe otomatik değer ekleyin
              </p>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => insertVariable('{{username}}')}
                  className="justify-start text-xs"
                >
                  {'{{username}}'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => insertVariable('{{firma}}')}
                  className="justify-start text-xs"
                >
                  {'{{firma}}'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => insertVariable('{{email}}')}
                  className="justify-start text-xs"
                >
                  {'{{email}}'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => insertVariable('{{tarih}}')}
                  className="justify-start text-xs"
                >
                  {'{{tarih}}'}
                </Button>
              </div>
              <div className="text-xs text-muted-foreground mt-2 pt-2 border-t">
                <p><strong>username:</strong> Kullanıcı adı</p>
                <p><strong>firma:</strong> Firma adı</p>
                <p><strong>email:</strong> Email adresi</p>
                <p><strong>tarih:</strong> Bugünün tarihi</p>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {/* Editor Content with Floating Menu */}
      <div ref={editorContainerRef} className="relative">
        {/* Floating Menu */}
        {editor && showFloatingMenu && floatingMenuTop !== null && (
          <div
            className="absolute z-50 flex items-center gap-0.5 p-1.5 bg-white dark:bg-slate-800 rounded-lg shadow-xl border border-slate-200 dark:border-slate-700 animate-in fade-in-0 zoom-in-95 duration-100 left-1/2 -translate-x-1/2"
            style={{ top: floatingMenuTop }}
          >
            {/* Text Formatting */}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onMouseDown={(e) => {
                e.preventDefault()
                editor.chain().focus().toggleBold().run()
              }}
              className={cn('h-7 w-7 p-0', editor.isActive('bold') && 'bg-slate-200 dark:bg-slate-600')}
              title="Kalın"
            >
              <Bold className="h-3.5 w-3.5" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onMouseDown={(e) => {
                e.preventDefault()
                editor.chain().focus().toggleItalic().run()
              }}
              className={cn('h-7 w-7 p-0', editor.isActive('italic') && 'bg-slate-200 dark:bg-slate-600')}
              title="İtalik"
            >
              <Italic className="h-3.5 w-3.5" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onMouseDown={(e) => {
                e.preventDefault()
                editor.chain().focus().toggleUnderline().run()
              }}
              className={cn('h-7 w-7 p-0', editor.isActive('underline') && 'bg-slate-200 dark:bg-slate-600')}
              title="Altı Çizili"
            >
              <UnderlineIcon className="h-3.5 w-3.5" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onMouseDown={(e) => {
                e.preventDefault()
                editor.chain().focus().toggleStrike().run()
              }}
              className={cn('h-7 w-7 p-0', editor.isActive('strike') && 'bg-slate-200 dark:bg-slate-600')}
              title="Üstü Çizili"
            >
              <Strikethrough className="h-3.5 w-3.5" />
            </Button>

            <div className="w-px h-5 bg-slate-300 mx-0.5" />

            {/* Headings */}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onMouseDown={(e) => {
                e.preventDefault()
                editor.chain().focus().toggleHeading({ level: 1 }).run()
              }}
              className={cn('h-7 w-7 p-0', editor.isActive('heading', { level: 1 }) && 'bg-slate-200 dark:bg-slate-600')}
              title="Başlık 1"
            >
              <Heading1 className="h-3.5 w-3.5" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onMouseDown={(e) => {
                e.preventDefault()
                editor.chain().focus().toggleHeading({ level: 2 }).run()
              }}
              className={cn('h-7 w-7 p-0', editor.isActive('heading', { level: 2 }) && 'bg-slate-200 dark:bg-slate-600')}
              title="Başlık 2"
            >
              <Heading2 className="h-3.5 w-3.5" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onMouseDown={(e) => {
                e.preventDefault()
                editor.chain().focus().toggleHeading({ level: 3 }).run()
              }}
              className={cn('h-7 w-7 p-0', editor.isActive('heading', { level: 3 }) && 'bg-slate-200 dark:bg-slate-600')}
              title="Başlık 3"
            >
              <Heading3 className="h-3.5 w-3.5" />
            </Button>

            <div className="w-px h-5 bg-slate-300 mx-0.5" />

            {/* Lists */}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onMouseDown={(e) => {
                e.preventDefault()
                editor.chain().focus().toggleBulletList().run()
              }}
              className={cn('h-7 w-7 p-0', editor.isActive('bulletList') && 'bg-slate-200 dark:bg-slate-600')}
              title="Madde İşaretli Liste"
            >
              <List className="h-3.5 w-3.5" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onMouseDown={(e) => {
                e.preventDefault()
                editor.chain().focus().toggleOrderedList().run()
              }}
              className={cn('h-7 w-7 p-0', editor.isActive('orderedList') && 'bg-slate-200 dark:bg-slate-600')}
              title="Numaralı Liste"
            >
              <ListOrdered className="h-3.5 w-3.5" />
            </Button>

            <div className="w-px h-5 bg-slate-300 mx-0.5" />

            {/* Alignment */}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onMouseDown={(e) => {
                e.preventDefault()
                editor.chain().focus().setTextAlign('left').run()
              }}
              className={cn('h-7 w-7 p-0', editor.isActive({ textAlign: 'left' }) && 'bg-slate-200 dark:bg-slate-600')}
              title="Sola Hizala"
            >
              <AlignLeft className="h-3.5 w-3.5" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onMouseDown={(e) => {
                e.preventDefault()
                editor.chain().focus().setTextAlign('center').run()
              }}
              className={cn('h-7 w-7 p-0', editor.isActive({ textAlign: 'center' }) && 'bg-slate-200 dark:bg-slate-600')}
              title="Ortala"
            >
              <AlignCenter className="h-3.5 w-3.5" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onMouseDown={(e) => {
                e.preventDefault()
                editor.chain().focus().setTextAlign('right').run()
              }}
              className={cn('h-7 w-7 p-0', editor.isActive({ textAlign: 'right' }) && 'bg-slate-200 dark:bg-slate-600')}
              title="Sağa Hizala"
            >
              <AlignRight className="h-3.5 w-3.5" />
            </Button>

            <div className="w-px h-5 bg-slate-300 mx-0.5" />

            {/* Image */}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onMouseDown={(e) => {
                e.preventDefault()
                fileInputRef.current?.click()
              }}
              className="h-7 w-7 p-0"
              title="Resim Ekle"
            >
              <ImageIcon className="h-3.5 w-3.5" />
            </Button>

            <div className="w-px h-5 bg-slate-300 mx-0.5" />

            {/* Callout Renkleri */}
            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault()
                editor.chain().focus().toggleCallout({ type: 'warning' }).run()
              }}
              className={cn('w-5 h-5 rounded', editor.isActive('callout', { type: 'warning' }) && 'ring-2 ring-amber-500')}
              style={{ backgroundColor: '#FEF3C7' }}
              title="Uyarı Kartı"
            />
            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault()
                editor.chain().focus().toggleCallout({ type: 'info' }).run()
              }}
              className={cn('w-5 h-5 rounded', editor.isActive('callout', { type: 'info' }) && 'ring-2 ring-blue-500')}
              style={{ backgroundColor: '#DBEAFE' }}
              title="Bilgi Kartı"
            />
            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault()
                editor.chain().focus().toggleCallout({ type: 'success' }).run()
              }}
              className={cn('w-5 h-5 rounded', editor.isActive('callout', { type: 'success' }) && 'ring-2 ring-green-500')}
              style={{ backgroundColor: '#D1FAE5' }}
              title="Başarı Kartı"
            />
            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault()
                editor.chain().focus().toggleCallout({ type: 'error' }).run()
              }}
              className={cn('w-5 h-5 rounded', editor.isActive('callout', { type: 'error' }) && 'ring-2 ring-red-500')}
              style={{ backgroundColor: '#FEE2E2' }}
              title="Hata Kartı"
            />
            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault()
                editor.chain().focus().unsetCallout().run()
              }}
              className="w-5 h-5 rounded border border-slate-300 flex items-center justify-center bg-white"
              title="Kartı Kaldır"
            >
              <X className="h-3 w-3 text-slate-500" />
            </button>
          </div>
        )}

        <EditorContent editor={editor} />
      </div>
    </div>
  )
}
